import { Controller } from "@hotwired/stimulus"
import { get, patch } from "@rails/request.js"

// First GOAT Marketplace sale CONGRATULATIONS popup (#19557), shown to the agent
// who made the sale — once.
// - On connect: fetches the user's not-yet-closed celebration (catch-up after a
//   reload or a sale posted while offline).
// - Live: refetches when the server pings `first_sale_celebration`.
// - X, "Keep It Going", Escape or a backdrop click closes it for good.
// - Fully disabled when AppSetting.first_sale_celebration_enabled is off.
// - A real modal while open: Tab cycles inside it, and the page behind it is
//   marked inert the way useBackgroundInert does (modal chrome and
//   [data-inert-exempt] overlay layers are skipped; only what we inerted is
//   released).
const INERT_SKIP_SELECTOR = ".modal, .modal-backdrop, [data-inert-exempt], script, style, link, template"
const FOCUSABLE_SELECTOR = "button, a[href], [tabindex]:not([tabindex='-1'])"

export default class extends Controller {
  static values = {
    url: String,
    acknowledgeUrl: String,
    imageUrl: String,
    disabled: Boolean
  }

  connect() {
    if (this.disabledValue) return

    this.onRealtime = (event) => {
      if (event.detail && event.detail.type === "first_sale_celebration") this.refresh()
    }
    this.onKeydown = (event) => {
      if (!this.overlay) return
      if (event.key === "Escape") this.close()
      else if (event.key === "Tab") this.trapTab(event)
    }
    document.addEventListener("goat:realtime-event", this.onRealtime)
    document.addEventListener("keydown", this.onKeydown)
    this.refresh()
  }

  disconnect() {
    if (this.onRealtime) document.removeEventListener("goat:realtime-event", this.onRealtime)
    if (this.onKeydown) document.removeEventListener("keydown", this.onKeydown)
    this.remove()
  }

  async refresh() {
    try {
      const response = await get(this.urlValue, { responseKind: "json" })
      if (!response.ok) return

      const data = await response.json
      if (data.celebration && !this.overlay) this.open(data.celebration)
    } catch (_error) {
      // Network blip — the next realtime ping or page load will retry.
    }
  }

  open(celebration) {
    const color = /^#[0-9a-f]{6}$/i.test(celebration.lead_type_color || "") ? celebration.lead_type_color : "#CD0200"
    this.overlay = document.createElement("div")
    this.overlay.className = "gl-first-sale"
    this.overlay.innerHTML = `
      <div class="gl-first-sale__dialog" role="dialog" aria-modal="true" aria-labelledby="gl-first-sale-title">
        <div class="gl-first-sale__art">
          <span class="gl-first-sale__confetti gl-first-sale__confetti--1" aria-hidden="true"></span>
          <span class="gl-first-sale__confetti gl-first-sale__confetti--2" aria-hidden="true"></span>
          <span class="gl-first-sale__confetti gl-first-sale__confetti--3" aria-hidden="true"></span>
          <span class="gl-first-sale__confetti gl-first-sale__confetti--4" aria-hidden="true"></span>
          <img class="gl-first-sale__image"
               alt="The GOAT in a suit wearing a gold number-one medal, fists raised in celebration">
          <button type="button" class="gl-first-sale__close" aria-label="Close" data-first-sale-close>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"></path></svg>
          </button>
        </div>
        <div class="gl-first-sale__body">
          <p class="gl-first-sale__eyebrow">Your first GOAT Leads sale</p>
          <h2 class="gl-first-sale__title" id="gl-first-sale-title">Congratulations!</h2>
          <p class="gl-first-sale__copy">You just posted your first sale with GOAT Leads. This is the first of many.</p>
          <div class="gl-first-sale__pill">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="9" r="6"></circle><path d="M8.5 14.5L7 22l5-3 5 3-1.5-7.5"></path></svg>
            <span class="gl-first-sale__pill-type">${this.escape(celebration.lead_type_label)}</span>
            <span aria-hidden="true">·</span>
            <span>Sold ${this.escape(this.formatDate(celebration.sold_on))}</span>
          </div>
          <div class="gl-first-sale__ap">
            <span class="gl-first-sale__ap-label">AP</span>
            <span class="gl-first-sale__ap-amount">${this.escape(this.formatMoney(celebration.annual_premium_cents))}</span>
          </div>
          <button type="button" class="gl-btn gl-btn-primary gl-first-sale__cta" data-first-sale-close>Keep It Going</button>
        </div>
      </div>`

    // Set from the DOM, never interpolated into markup.
    this.overlay.querySelector(".gl-first-sale__image").src = this.imageUrlValue
    const pill = this.overlay.querySelector(".gl-first-sale__pill")
    pill.style.background = color
    pill.style.borderColor = color
    pill.style.color = this.textColorOn(color)

    this.overlay.addEventListener("click", (event) => {
      if (event.target === this.overlay || event.target.closest("[data-first-sale-close]")) this.close()
    })
    this.element.appendChild(this.overlay)
    this.inertBackground()
    this.overlay.querySelector(".gl-first-sale__cta")?.focus()
    requestAnimationFrame(() => this.overlay?.classList.add("is-visible"))
  }

  async close() {
    this.remove()
    try {
      await patch(this.acknowledgeUrlValue, { responseKind: "json" })
    } catch (_error) {
      // If the close fails it simply re-shows on next load — acceptable.
    }
  }

  remove() {
    if (this.overlay) this.overlay.remove()
    this.overlay = null
    this.releaseBackground()
  }

  // Keeps Tab and Shift+Tab inside the dialog.
  trapTab(event) {
    const focusables = Array.from(this.overlay.querySelectorAll(FOCUSABLE_SELECTOR))
    if (focusables.length === 0) return

    const first = focusables[0]
    const last = focusables[focusables.length - 1]
    const active = document.activeElement
    const outside = !this.overlay.contains(active)

    if (event.shiftKey && (active === first || outside)) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && (active === last || outside)) {
      event.preventDefault()
      first.focus()
    }
  }

  inertBackground() {
    this.inertedByUs = []
    document.querySelectorAll("body > *").forEach((element) => {
      if (element === this.element || element.contains(this.element)) return
      if (element.matches(INERT_SKIP_SELECTOR) || element.hasAttribute("inert")) return

      element.setAttribute("inert", "")
      this.inertedByUs.push(element)
    })
  }

  releaseBackground() {
    (this.inertedByUs || []).forEach((element) => element.removeAttribute("inert"))
    this.inertedByUs = []
  }

  // Black or white text, whichever reads on the lead type's color.
  textColorOn(hex) {
    const match = /^#?([0-9a-f]{6})$/i.exec(hex || "")
    if (!match) return "#FFFFFF"
    const value = parseInt(match[1], 16)
    const channel = (shift) => {
      const c = ((value >> shift) & 255) / 255
      return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
    }
    const luminance = 0.2126 * channel(16) + 0.7152 * channel(8) + 0.0722 * channel(0)
    return luminance > 0.179 ? "#0F172A" : "#FFFFFF"
  }

  // "2026-10-27" → "10/27/2026" without new Date() (no UTC day shift).
  formatDate(isoDate) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate || "")
    return match ? `${match[2]}/${match[3]}/${match[1]}` : ""
  }

  formatMoney(cents) {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format((cents || 0) / 100)
  }

  escape(value) {
    const div = document.createElement("div")
    div.textContent = value == null ? "" : String(value)
    return div.innerHTML
  }
}
